import React, { useState } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { Loader2 } from 'lucide-react';
import { auth } from '../firebaseInstance';

// Default to Sign Up mode when arriving with ?mode=signup, otherwise default
// to Sign In. New users clicking "Create Persona" / "Claim Your Handle" /
// "Start in 2 Minutes" land in signup; everyone else (direct /auth visit,
// "Login" navbar) lands in signin.
const initialModeIsLogin = () => {
  if (typeof window === 'undefined') return true;
  const params = new URLSearchParams(window.location.search);
  return params.get('mode') !== 'signup';
};

export const AuthView = ({ onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(initialModeIsLogin);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
      // onAuthSuccess will be called automatically via onAuthStateChanged
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
        setError('Invalid email or password. Please try again.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a moment and try again.');
      } else if (code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Try signing in instead.');
      } else if (code === 'auth/weak-password') {
        setError('Password must be at least 6 characters.');
      } else {
        setError('An error occurred. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);

    try {
      // Suppress COOP warnings from Firebase Auth popup handling
      const originalWarn = console.warn;
      const suppressedWarnings = [];
      console.warn = (...args) => {
        const message = args.join(' ');
        // Suppress Cross-Origin-Opener-Policy warnings from Firebase Auth
        if (message.includes('Cross-Origin-Opener-Policy') || 
            message.includes('window.closed')) {
          suppressedWarnings.push(message);
          return; // Suppress this warning
        }
        originalWarn.apply(console, args);
      };

      const provider = new GoogleAuthProvider();
      // Request additional scopes for profile info
      provider.addScope('profile');
      provider.addScope('email');
      
      const result = await signInWithPopup(auth, provider);
      
      // Restore original console.warn
      console.warn = originalWarn;
      
      // onAuthSuccess will be called automatically via onAuthStateChanged
      
      // For new users, we'll pre-populate profile data in the main App component
      // when the user document doesn't exist yet
    } catch (err) {
      // Restore original console.warn in case of error
      if (console.warn !== console.warn) {
        console.warn = originalWarn;
      }
      
      if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account with this email already exists. Please sign in with email/password first.');
      } else if (err.code === 'auth/popup-closed-by-user') {
        // User closed the popup - don't show error
        setError('');
      } else {
        setError(err.message || 'Failed to sign in with Google. Please try again.');
      }
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#000000] px-4 relative overflow-hidden">
      {/* Neural Grid Background */}
      <div 
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ 
          backgroundImage: 'radial-gradient(circle at 2px 2px, #C1FF00 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}
      ></div>

      {/* Noise Overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }}
      ></div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-[#0a0a0a] backdrop-blur-sm rounded-2xl shadow-[0_0_100px_rgba(193,255,0,0.1)] p-8 sm:p-10 border-2 border-[#C1FF00]/20">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="relative inline-block">
                <span className="ai-shimmer text-3xl font-bold relative inline-block font-['Syne',sans-serif]" data-text="PerAI.me">
                  <span className="ai-shimmer-particles">
                    {/* Sparkling particles */}
                    {[...Array(12)].map((_, i) => {
                      const angle = (i * 30) * (Math.PI / 180);
                      const radius = 60 + (i % 3) * 15;
                      const tx = Math.cos(angle) * radius;
                      const ty = Math.sin(angle) * radius;
                      return (
                        <span
                          key={i}
                          className="ai-shimmer-particle"
                          style={{
                            '--tx': `${tx}px`,
                            '--ty': `${ty}px`,
                            left: '50%',
                            top: '50%',
                            animationDelay: `${i * 0.2}s`,
                            animationDuration: `${2 + (i % 3) * 0.5}s`
                          }}
                        />
                      );
                    })}
                  </span>
                  {/* Swirling trails */}
                  {[...Array(3)].map((_, i) => (
                    <span
                      key={i}
                      className="ai-shimmer-trail"
                      style={{
                        animationDelay: `${i * 1.3}s`,
                        animationDuration: `${4 + i * 0.5}s`
                      }}
                    />
                  ))}
                  PerAI.me
                </span>
              </div>
            </div>
            <p className="text-white/80 text-sm font-black uppercase tracking-widest font-['Courier New',monospace]">
              {isLogin ? 'Welcome back!' : 'Create your AI profile'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-3 bg-red-900/20 border-2 border-red-500/50 rounded-xl text-red-400 text-sm font-['Courier New',monospace]">
              {error}
            </div>
          )}

          {/* Google Sign In Button - MOVED TO TOP */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading || googleLoading}
            className="w-full flex items-center justify-center gap-3 bg-black border-2 border-white/20 hover:border-[#C1FF00] text-white font-black uppercase py-3 rounded-xl transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed hover:text-[#C1FF00] font-['Courier New',monospace] text-xs tracking-widest"
          >
            {googleLoading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Sign in with Google</span>
              </>
            )}
          </button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t-2 border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0a0a0a] text-gray-500 font-black uppercase tracking-widest text-xs font-['Courier New',monospace]">Or continue with</span>
            </div>
          </div>

          {/* Form - MOVED BELOW GOOGLE SIGN IN */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-white/60 mb-2 font-['Courier New',monospace]">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border-2 border-white/10 rounded-xl focus:ring-2 focus:ring-[#C1FF00] focus:border-[#C1FF00] bg-black/50 outline-none transition-all placeholder:text-gray-600 text-white font-['Courier New',monospace]"
                placeholder="you@example.com"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-white/60 mb-2 font-['Courier New',monospace]">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-3 border-2 border-white/10 rounded-xl focus:ring-2 focus:ring-[#C1FF00] focus:border-[#C1FF00] bg-black/50 outline-none transition-all placeholder:text-gray-600 text-white font-['Courier New',monospace]"
                placeholder="••••••••"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full bg-[#C1FF00] hover:bg-[#C1FF00]/90 text-black font-black uppercase py-3 rounded-xl transition-all shadow-[0_4px_0_0_#C1FF00] hover:shadow-[0_2px_0_0_#C1FF00] active:shadow-[0_1px_0_0_#C1FF00] active:translate-y-[3px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none font-['Courier New',monospace] text-sm tracking-widest"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <Loader2 size={20} className="animate-spin mr-2" />
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          {/* Toggle Login/Signup */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setEmail('');
                setPassword('');
              }}
              className="text-sm text-gray-500 hover:text-[#C1FF00] transition-colors font-['Courier New',monospace] uppercase tracking-widest"
              disabled={loading}
            >
              {isLogin ? (
                <>Don't have an account? <span className="font-black text-[#C1FF00]">Sign up</span></>
              ) : (
                <>Already have an account? <span className="font-black text-[#C1FF00]">Sign in</span></>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-600 mt-6 font-['Courier New',monospace] uppercase tracking-widest">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

